import { useParams, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, EmptyState } from "../../components/ui";
import { useShipmentByReference } from "../../features/shipments/queries";
import { CARRIER_REFETCH_MS } from "../../features/shipments/carrierTracking";
import ClientViewPanel from "./components/ClientViewPanel";
import ShipmentDetailView from "./components/ShipmentDetailView";

export default function EnviosDetailPage() {
  const { t } = useTranslation("envios");
  const { guia } = useParams<{ guia: string }>();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<"internal" | "client">("internal");
  // Se refresca cada minuto mientras está abierto: la paquetería (17TRACK) y
  // el chofer pueden mover el estatus sin que nadie toque esta pantalla.
  const { shipment, isLoading, isError } = useShipmentByReference(guia, {
    refetchInterval: CARRIER_REFETCH_MS,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-app">
        <div className="flex min-h-64 items-center justify-center py-12">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500" />
        </div>
      </div>
    );
  }

  if (isError || !shipment) {
    return (
      <div className="min-h-screen bg-app px-4 py-12">
        <EmptyState
          icon="ri-error-warning-line"
          title={t("detail.notFound")}
          description={
            <>
              {t("detail.notFoundDescription")}{" "}
              <span className="font-mono font-semibold">{guia}</span>
            </>
          }
          action={
            <Button
              size="sm"
              icon={<i className="ri-arrow-left-line" />}
              onClick={() => navigate("/envios/todos")}
            >
              {t("detail.backToShipments")}
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-app">
      <div className="flex flex-col px-6 py-4 gap-4">
        <div className="flex items-center justify-between gap-4">
          <Button
            variant="outline"
            size="sm"
            icon={<i className="ri-arrow-left-line" />}
            onClick={() => navigate("/envios/todos")}
          >
            {t("title")}
          </Button>

          <div className="flex items-center gap-2 bg-subtle rounded-xl p-1 flex-shrink-0 border border-line">
            <Button
              variant={viewMode === "internal" ? "primary" : "ghost"}
              size="sm"
              icon={<i className="ri-tools-line" />}
              onClick={() => setViewMode("internal")}
            >
              {t("detail.internalPanel")}
            </Button>
            <Button
              variant={viewMode === "client" ? "primary" : "ghost"}
              size="sm"
              icon={<i className="ri-eye-line" />}
              onClick={() => setViewMode("client")}
            >
              {t("detail.clientView")}
            </Button>
          </div>
        </div>
      </div>

      <div
        className={`mx-auto px-6 pb-6 pt-2 ${
          viewMode === "internal" ? "max-w-7xl" : "max-w-4xl"
        }`}
      >
        {viewMode === "internal" ? (
          <ShipmentDetailView shipment={shipment} />
        ) : (
          <ClientViewPanel shipment={shipment} />
        )}
      </div>
    </div>
  );
}
